// React
import React, { useEffect, useState } from "react";

// Internal
import { getDataTestId } from "../functions/data-test-ids";

// CSS
import "./color-picker.css";

const HEX_CHARS = "0123456789abcdefABCDEF";
const NAV_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Backspace", "Delete", "Tab", "Home", "End", "Escape"];

function isHexChar(c) {
    return HEX_CHARS.includes(c);
}

function isValidHex(value) {
    if (typeof value !== "string" || value.length !== 6) {
        return false;
    }
    for (let i = 0; i < value.length; i++) {
        if (!isHexChar(value[i])) {
            return false;
        }
    }
    return true;
}

function sanitizeHex(value) {
    if (typeof value !== "string") return "";
    let result = "";
    for (let i = 0; i < value.length && result.length < 6; i++) {
        if (isHexChar(value[i])) {
            result += value[i];
        }
    }
    return result;
}

function ColorPicker(props) {
    const [textValue, setTextValue] = useState(sanitizeHex(props.Value));

    useEffect(() => {
        setTextValue(sanitizeHex(props.Value));
    }, [props.Value]);

    const emitChange = (value) => {
        if (typeof props.OnChange === "function") {
            props.OnChange(value);
        }
    };

    const emitUpdate = (value) => {
        if (typeof props.OnUpdate === "function") {
            props.OnUpdate(value);
        }
    };

    const hexFromColorEvent = (e) => {
        const raw = e.target.value;
        return raw && raw.length > 0 ? raw.slice(1) : "000000";
    };

    const onColorChange = (e) => {
        const value = hexFromColorEvent(e);
        setTextValue(value);
        emitChange(value);
    };

    const onColorBlur = (e) => {
        emitUpdate(hexFromColorEvent(e));
    };

    const onTextKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey) return;
        if (NAV_KEYS.includes(e.key)) return;
        if (e.key === "Enter") {
            e.preventDefault();
            e.target.blur();
            return;
        }
        if (e.key.length === 1 && !isHexChar(e.key)) {
            e.preventDefault();
        }
    };

    const onTextChange = (e) => {
        const filtered = sanitizeHex(e.target.value);
        setTextValue(filtered);
        if (isValidHex(filtered)) {
            emitChange(filtered);
        }
    };

    const onTextBlur = () => {
        if (isValidHex(textValue)) {
            emitUpdate(textValue);
        } else {
            setTextValue(sanitizeHex(props.Value));
        }
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    const swatchColor = isValidHex(textValue)
        ? textValue
        : (isValidHex(props.Value) ? props.Value : "000000");

    const containerClass = props.ContainerClass || "eone-ui-color-picker-container";

    if (props.ReadOnly) {
        return (
            <div className={`${containerClass} read-only`}>
                <div
                    className="eone-ui-color-picker-swatch-readonly"
                    style={{ backgroundColor: `#${swatchColor}` }}
                    data-testid={getDataTestId(props.DataTestId, "swatch")} />
                <span
                    className="eone-ui-color-picker-hash"
                    aria-hidden="true">
                    #
                </span>
                <span
                    className="eone-ui-color-picker-hex-readonly"
                    data-testid={getDataTestId(props.DataTestId, "hex")}>
                    {textValue.toUpperCase()}
                </span>
            </div>
        );
    }

    return (
        <div className={containerClass}>
            <input
                type="color"
                className="eone-ui-color-picker-swatch"
                value={`#${swatchColor}`}
                onChange={onColorChange}
                onBlur={onColorBlur}
                aria-label="Open color picker"
                data-testid={getDataTestId(props.DataTestId, "swatch")} />
            <span className="eone-ui-color-picker-hash" aria-hidden="true">#</span>
            <input
                id={props.Id}
                type="text"
                className="eone-ui-color-picker-hex"
                autoFocus={props.AutoFocus}
                maxLength={6}
                value={textValue}
                onChange={onTextChange}
                onKeyDown={onTextKeyDown}
                onBlur={onTextBlur}
                spellCheck={false}
                autoComplete="off"
                aria-label="Hex color value"
                data-testid={getDataTestId(props.DataTestId, "hex")} />
        </div>
    );
}

export { ColorPicker };
